import { useState } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import TextEdit from '../../../molecules/TextEdit';
import SelectTextField from '../../../molecules/SelectTextField';
import CurrencyInput from '../../../molecules/CurrencyInput';
import DateField from '../../../molecules/DateField';
import { getErrorMessage } from '../../../../utils/formErrorUtils';
import {
    BusinessReference,
    type ClaimItem,
    type CreateClaimItemPayload,
    type ObligationType,
    type SelectOption,
} from '../../../../types/LegalCase';
import type { ErrorField } from '../../../../types/ErrorField';

interface LegalCaseClaimItemFormProps {
    businessReferenceOptions: SelectOption[];
    obligationTypeOptions: SelectOption[];
    errorFields: ErrorField[];
    isSaving: boolean;
    onSubmit: (value: CreateClaimItemPayload) => Promise<boolean>;
    onCancel: () => void;
    // Título sendo editado (AF-3691). Ausente = adição. Quando presente, os campos nascem
    // preenchidos e o botão muda de rótulo — o mesmo form serve aos dois fluxos.
    initialItem?: ClaimItem | null;
}

// Campos que têm binding de erro inline (errorMessage=) no JSX. O bloco residual mostra só os
// erros SEM campo próprio (ex.: "items" da regra anti-duplicação INSTALLMENT+GUARANTEE), para
// não duplicar (inline + residual). Precisa espelhar exatamente os campos com errorMessage abaixo.
const inlineErrorFields = new Set([
    'titleNumber', 'businessReference', 'amountAtFiling',
    'dueDate', 'obligationType', 'externalReference', 'description',
]);
const residualErrors = (errorFields: ErrorField[]): ErrorField[] =>
    errorFields.filter((e) => !inlineErrorFields.has(e.fieldName));

// Formulário de um título do claim: adiciona (sem `initialItem`) ou edita (com `initialItem`).
// Só JSX + estado local simples (o form é curto e linear). A regra do GUARANTEE vive no
// backend — inclusive na edição; o form só oferece o select e mostra o 422.
const LegalCaseClaimItemForm = ({
    businessReferenceOptions,
    obligationTypeOptions,
    errorFields,
    isSaving,
    onSubmit,
    onCancel,
    initialItem,
}: LegalCaseClaimItemFormProps) => {
    const isEditing = !!initialItem;
    const [titleNumber, setTitleNumber] = useState(initialItem?.titleNumber ?? '');
    const [businessReference, setBusinessReference] = useState<BusinessReference>(
        (initialItem?.businessReference as BusinessReference) ?? BusinessReference.INSTALLMENT,
    );
    const [amountAtFiling, setAmountAtFiling] = useState(initialItem?.amountAtFiling ?? 0);
    const [dueDate, setDueDate] = useState(initialItem?.dueDate ?? '');
    // `paidAmount` saiu do item na Task 18: o pago virou soma de lançamentos (aba Pagamentos), não
    // um campo digitado no título. O backend não aceita mais escrever aqui.
    const [obligationType, setObligationType] = useState<ObligationType | ''>(
        (initialItem?.obligationType as ObligationType) ?? '',
    );
    const [externalReference, setExternalReference] = useState(initialItem?.externalReference ?? '');
    const [description, setDescription] = useState(initialItem?.description ?? '');

    const canSubmit = titleNumber.trim().length > 0 && amountAtFiling > 0;

    const handleSubmit = async () => {
        // Na edição os campos opcionais vão como null (não undefined) para **limpar** de fato:
        // o PUT tem semântica de patch e ignora chave ausente, então `undefined` manteria o
        // valor antigo — apagar o vencimento ou desvincular a operação não funcionaria.
        const emptyValue = isEditing ? null : undefined;
        const ok = await onSubmit({
            titleNumber: titleNumber.trim(),
            businessReference,
            amountAtFiling,
            dueDate: dueDate || emptyValue,
            obligationType: obligationType || emptyValue,   // '' → limpa/omite conforme o fluxo
            externalReference: externalReference.trim() || emptyValue,
            description: description.trim() || emptyValue,
        });
        if (ok) onCancel();
    };

    return (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <TextEdit
                    label="Número do título"
                    value={titleNumber}
                    onChange={(e: any) => setTitleNumber(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'titleNumber')}
                />
                <SelectTextField
                    label="Referência de negócio"
                    items={businessReferenceOptions}
                    value={businessReference}
                    onChange={(e) => setBusinessReference(e.target.value as BusinessReference)}
                    errorMessage={getErrorMessage(errorFields, 'businessReference')}
                />
            </div>

            {businessReference === BusinessReference.GUARANTEE && (
                <p className="text-xs text-amber-700 dark:text-amber-400">
                    Garantia entra à parte, fora do total. Não pode conviver com uma parcela da
                    mesma operação.
                </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <CurrencyInput
                    label="Valor no ajuizamento"
                    prefix="R$"
                    fractionDigits={2}
                    value={amountAtFiling}
                    onChange={setAmountAtFiling}
                    errorMessage={getErrorMessage(errorFields, 'amountAtFiling')}
                />
                <DateField
                    label="Vencimento (opcional)"
                    value={dueDate}
                    onChange={setDueDate}
                    errorMessage={getErrorMessage(errorFields, 'dueDate')}
                />
                <SelectTextField
                    label="Tipo da obrigação (opcional)"
                    items={[{ value: '', label: '—' }, ...obligationTypeOptions]}
                    value={obligationType}
                    // O select só oferece '' (—) ou os valores do enum, então o cast é seguro.
                    onChange={(e) => setObligationType(e.target.value as ObligationType | '')}
                    errorMessage={getErrorMessage(errorFields, 'obligationType')}
                />
            </div>

            <TextEdit
                label="Referência externa (CPR-F, CD, PJ, operação)"
                value={externalReference}
                onChange={(e: any) => setExternalReference(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'externalReference')}
            />

            <TextEdit
                label="Observação"
                value={description}
                onChange={(e: any) => setDescription(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'description')}
            />

            {residualErrors(errorFields).map((e) => (
                <div key={e.fieldName} className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                    <FiAlertTriangle size={14} />
                    <span>{e.message}</span>
                </div>
            ))}

            <div className="flex items-center justify-end gap-2">
                <button
                    onClick={onCancel}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                >
                    Cancelar
                </button>
                <button
                    onClick={handleSubmit}
                    disabled={isSaving || !canSubmit}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                    {isSaving ? 'Salvando...' : isEditing ? 'Salvar título' : 'Adicionar título'}
                </button>
            </div>
        </div>
    );
};

export default LegalCaseClaimItemForm;
